import {
  Color3,
  MeshBuilder,
  Scene,
  StandardMaterial,
  type Mesh,
} from '@babylonjs/core';

export type BeamKind = 'pickup' | 'dropoff' | 'premium-pickup';

const COLORS: Record<BeamKind, Color3> = {
  pickup: new Color3(1.0, 0.78, 0.19),         // #ffc830
  dropoff: new Color3(0.36, 0.90, 0.54),       // #5be58a
  'premium-pickup': new Color3(1.0, 0.54, 0.23), // #ff8a3a
};

const BEAM_RADIUS = 4;
const BEAM_HEIGHT = 200;
const BEAM_BASE_Y = 100; // height offset above the ground sample

/** A translucent emissive cylinder that pulses in alpha. Used to mark pickup/dropoff
 *  zones above their buildings in the 3D world. */
export class Beam {
  private mesh: Mesh;
  private mat: StandardMaterial;
  private kind: BeamKind;
  private nextId: number;
  private elapsed = 0;
  private baseAlpha: { min: number; max: number };

  constructor(scene: Scene, kind: BeamKind, x: number, groundY: number, z: number, idSuffix: number) {
    this.kind = kind;
    this.nextId = idSuffix;
    const name = `beam-${kind}-${idSuffix}`;
    this.mesh = MeshBuilder.CreateCylinder(
      name,
      { height: BEAM_HEIGHT, diameter: BEAM_RADIUS * 2, tessellation: 12 },
      scene,
    );
    this.mesh.position.set(x, groundY + BEAM_BASE_Y, z);
    this.mesh.isPickable = false;
    this.mesh.checkCollisions = false;

    this.mat = new StandardMaterial(`${name}-mat`, scene);
    const col = COLORS[kind];
    this.mat.emissiveColor = col;
    this.mat.diffuseColor = col;
    this.mat.specularColor = new Color3(0, 0, 0);
    this.mat.disableLighting = true;
    this.mat.backFaceCulling = false;
    this.mat.alpha = 0.35;
    this.mesh.material = this.mat;
    this.baseAlpha = { min: 0.2, max: 0.5 };
  }

  /** Advance the pulse animation. */
  update(dt: number): void {
    this.elapsed += dt;
    const t = (Math.sin(this.elapsed * 2.0) + 1) * 0.5; // 0..1
    this.mat.alpha = this.baseAlpha.min + (this.baseAlpha.max - this.baseAlpha.min) * t;
  }

  /** Move the beam in case the ground sample changes (lazy chunk load). */
  setPosition(x: number, groundY: number, z: number): void {
    this.mesh.position.set(x, groundY + BEAM_BASE_Y, z);
  }

  dispose(): void {
    this.mesh.dispose();
    this.mat.dispose();
  }

  /** Beam identity for debugging. */
  get id(): number {
    return this.nextId;
  }

  /** Beam kind — useful when swapping pickup→dropoff. */
  get currentKind(): BeamKind {
    return this.kind;
  }
}
